import React, { useState } from "react";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import useContacts from "../../../services/chat/useContacts";

const C = {
  green: "#25d366",
  greenDk: "#128c7e",
  bg: "#f0f2f5",
  white: "#ffffff",
  border: "#d1d5db",
  text: "#111b21",
  sub: "#667781",
  muted: "#8696a0",
  red: "#e53e3e",
  blue: "#1877f2",
  blueBg: "#f0f4ff",
};

const inp = {
  width: "100%",
  padding: "9px 12px",
  border: `1px solid ${C.border}`,
  borderRadius: 8,
  fontSize: 13.5,
  color: C.text,
  outline: "none",
  boxSizing: "border-box",
  background: C.white,
};

const ContactAdd = ({ userID }) => {
  const { addContact, uploadBulkContacts, getContacts } = useContacts();

  const [selectedMethod, setSelectedMethod] = useState("1");
  const [contactName, setContactName] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [gender, setGender] = useState("");
  const [tags, setTags] = useState([""]);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // ── Tags ──
  const addTag = () => setTags([...tags, ""]);
  const removeTag = (i) => {
    const u = [...tags];
    u.splice(i, 1);
    setTags(u);
  };
  const handleTagChange = (i, v) => {
    const u = [...tags];
    u[i] = v;
    setTags(u);
  };

  // ── Add single contact ──
  const handleAddContact = async () => {
    if (!contactName) {
      toast.error("Please enter name");
      return;
    }

    if (!contactNumber || contactNumber.length !== 10) {
      toast.error("Enter valid mobile number");
      return;
    }

    try {
      setLoading(true);

      const res = await addContact({
        contactNumber,
        contactEmail,
        contactName,
        gender,
        tags: tags.filter((tag) => tag.trim() !== ""),
      });

      if (res?.responseCode === "00") {
        toast.success(res.responseMessage || "Contact added successfully");

        // Call getContacts API after successful add
        await getContacts?.();

        // reset form
        setContactName("");
        setContactNumber("");
        setContactEmail("");
        setGender("");
        setTags([""]);
      } else {
        toast.error(res?.responseMessage || "Failed to add contact");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // ── Process file ──
  const processFile = (selectedFile) => {
    if (!selectedFile) return;
    if (
      !selectedFile.name.endsWith(".xlsx") &&
      !selectedFile.name.endsWith(".csv")
    ) {
      toast.error("Please upload .xlsx or .csv file");
      return;
    }
    setFile(selectedFile);
  };

  // ── Bulk upload ──
  const handleFileUpload = () => {
    if (!file) {
      toast.error("Upload Excel file");
      return;
    }
    setLoading(true);
    const reader = new FileReader();
    reader.onload = async (e) => {
      const wb = XLSX.read(new Uint8Array(e.target.result), { type: "array" });
      const json = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);
      const formatted = json.map((item) => ({
        userid: userID,
        contactNumber: item.contactNumber,
        contactEmail: item.contactEmail,
        contactName: item.contactName,
        gender: item.gender,
        tags: item.tags ? item.tags.split(",").map((t) => t.trim()) : [],
      }));
      try {
        const res = await uploadBulkContacts(formatted);
        if (res.responseCode === "00") {
          toast.success(res.responseMessage);
          // fetchChatContacts();
          setFile(null);
        } else toast.warning(res.responseMessage);
      } catch {
        toast.error("Upload failed");
      }
      setLoading(false);
    };
    reader.readAsArrayBuffer(file);
  };

  // ── Download sample CSV ──
  const handleDownloadSample = () => {
    const headers = [
      "contactName",
      "contactNumber",
      "contactEmail",
      "gender",
      "tags",
    ];
    const sample = [
      "John Doe",
      "9876543210",
      "john@example.com",
      "Male",
      "vip,customer",
    ];
    const csv = [headers.join(","), sample.join(",")].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sample_contacts.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div
        className="montserrat-font-1 flex flex-col h-full"
        style={{ background: C.white }}
      >
        {/* Header */}
        <div
          className="px-7 pt-[22px] pb-[14px]"
          style={{
            borderBottom: `1px solid ${C.border}`,
          }}
        >
          <p
            className="text-[18px] font-bold mb-[6px]"
            style={{ color: C.text }}
          >
            Add Customer Contacts
          </p>

          <p className="text-[13px] leading-[1.6] m-0" style={{ color: C.sub }}>
            Please provide the contact number you wish to add. This will help us
            keep in touch with you for updates and important information.
          </p>
        </div>

        {/* Form */}
        <div className="flex-1 px-7 py-[22px]">
          {/* Radio */}
          <div className="flex gap-7 mb-[22px]">
            <label
              className="flex items-center gap-2 text-[14px] cursor-pointer"
              style={{ color: C.text }}
            >
              <input
                type="radio"
                checked={selectedMethod === "1"}
                onChange={() => setSelectedMethod("1")}
                className="w-4 h-4"
                style={{ accentColor: C.green }}
              />
              One By One
            </label>

            <label
              className="flex items-center gap-2 text-[14px] cursor-pointer"
              style={{ color: C.text }}
            >
              <input
                type="radio"
                checked={selectedMethod === "2"}
                onChange={() => setSelectedMethod("2")}
                className="w-4 h-4"
                style={{ accentColor: C.green }}
              />
              Bulk Upload
            </label>
          </div>

          {/* ══ ONE BY ONE ══ */}
          {selectedMethod === "1" && (
            <>
              {/* Name + Mobile — same row */}
              <div className="montserrat-font-1 grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label
                    className="block text-[13px] font-semibold mb-1.5"
                    style={{ color: C.text }}
                  >
                    Name <span style={{ color: C.red }}>*</span>
                  </label>

                  <form autoComplete="off">
                    <input
                      placeholder="Enter contact name."
                      value={contactName}
                      onChange={(e) => setContactName(e.target.value)}
                      onFocus={(e) => (e.target.style.borderColor = C.green)}
                      onBlur={(e) => (e.target.style.borderColor = C.border)}
                      style={inp}
                    />
                  </form>
                </div>

                <div>
                  <label
                    className="block text-[13px] font-semibold mb-1.5"
                    style={{ color: C.text }}
                  >
                    Mobile <span style={{ color: C.red }}>*</span>
                  </label>

                  <form autoComplete="off">
                    <input
                      placeholder="Enter mobile number."
                      value={contactNumber}
                      onChange={(e) =>
                        setContactNumber(e.target.value.replace(/\D/g, ""))
                      }
                      onFocus={(e) => (e.target.style.borderColor = C.green)}
                      onBlur={(e) => (e.target.style.borderColor = C.border)}
                      style={inp}
                    />
                  </form>
                </div>
              </div>

              {/* Email + Gender — same row */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label
                    className="block text-[13px] font-semibold mb-1.5"
                    style={{ color: C.text }}
                  >
                    E-mail
                  </label>

                  <form autoComplete="off">
                    <input
                      placeholder="Enter e-mail id."
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                      onFocus={(e) => (e.target.style.borderColor = C.green)}
                      onBlur={(e) => (e.target.style.borderColor = C.border)}
                      style={inp}
                    />
                  </form>
                </div>

                <div>
                  <label
                    className="block text-[13px] font-semibold mb-1.5"
                    style={{ color: C.text }}
                  >
                    Gender
                  </label>

                  <select
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                    className="cursor-pointer"
                    style={{
                      ...inp,
                      color: gender ? C.text : C.muted,
                    }}
                  >
                    <option value="">Select Gender</option>
                    <option>Male</option>
                    <option>Female</option>
                    <option>Other</option>
                  </select>
                </div>
              </div>

              {/* Tags — label + inputs in same grid row alignment */}
              <div className="mb-6">
                <label
                  className="block text-[13px] font-semibold mb-2"
                  style={{ color: C.text }}
                >
                  Tags
                </label>

                <div className="grid grid-cols-2 gap-4 items-start">
                  {tags.map((tag, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <input
                        placeholder="Enter Tag Name"
                        value={tag}
                        onChange={(e) => handleTagChange(i, e.target.value)}
                        onFocus={(e) => (e.target.style.borderColor = C.green)}
                        onBlur={(e) => (e.target.style.borderColor = C.border)}
                        style={inp}
                      />

                      <button
                        onClick={() => removeTag(i)}
                        className="shrink-0 w-7 h-7 rounded-full border flex items-center justify-center text-[14px] cursor-pointer"
                        style={{
                          borderColor: C.border,
                          background: C.bg,
                          color: C.muted,
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}

                  {/* Add More Tag always appears in next grid slot */}
                  <div className="flex items-center h-[42px]">
                    <button
                      onClick={addTag}
                      className="px-[14px] py-2 rounded-lg border text-[13px] font-medium cursor-pointer"
                      style={{
                        borderColor: C.border,
                        background: C.white,
                        color: C.sub,
                      }}
                    >
                      + Add More Tag
                    </button>
                  </div>
                </div>
              </div>

              {/* Save */}
              <button
                onClick={handleAddContact}
                disabled={loading}
                className="px-9 py-[11px] rounded-[9px] border-0 text-[15px] font-semibold shadow-[0_3px_14px_rgba(37,211,102,0.35)]"
                style={{
                  background: `linear-gradient(135deg,${C.green},${C.greenDk})`,
                  color: C.white,
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.7 : 1,
                }}
              >
                {loading ? "Saving..." : "Save"}
              </button>
            </>
          )}

          {/* ══ BULK UPLOAD ══ */}
          {selectedMethod === "2" && (
            <div className="flex flex-col gap-5">
              {/* Drag & Drop upload area */}
              <label
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  processFile(e.dataTransfer.files[0]);
                }}
                className="flex flex-col items-center justify-center gap-2.5 px-6 py-12 rounded-xl cursor-pointer transition-all duration-150"
                style={{
                  border: `1.5px dashed ${dragOver ? C.blue : "#93c5fd"}`,
                  background: dragOver ? "#eff6ff" : C.blueBg,
                }}
              >
                {/* Upload icon */}
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={C.blue}
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="16 16 12 12 8 16" />
                  <line x1="12" y1="12" x2="12" y2="21" />
                  <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
                </svg>

                <span
                  className="text-[14px] font-semibold"
                  style={{ color: C.blue }}
                >
                  {file ? file.name : "Click to upload Excel file"}
                </span>

                <input
                  type="file"
                  accept=".xlsx,.csv"
                  className="hidden"
                  onChange={(e) => processFile(e.target.files[0])}
                />
              </label>

              {/* Bottom row: hint + download sample */}
              <div className="flex items-center justify-between">
                <span className="text-[13px]" style={{ color: C.sub }}>
                  You can download a CSV sample for your reference
                </span>

                <button
                  onClick={handleDownloadSample}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg border text-[13px] font-medium cursor-pointer"
                  style={{
                    borderColor: C.border,
                    background: C.white,
                    color: C.sub,
                  }}
                >
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Download Sample Excel
                </button>
              </div>

              {/* Save / Upload button */}
              <button
                onClick={handleFileUpload}
                disabled={loading}
                className="self-start px-9 py-[11px] rounded-[9px] border-0 text-[15px] font-semibold shadow-[0_3px_14px_rgba(37,211,102,0.35)]"
                style={{
                  background: `linear-gradient(135deg,${C.green},${C.greenDk})`,
                  color: C.white,
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.7 : 1,
                }}
              >
                {loading ? "Uploading…" : "Save"}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ContactAdd;
